// frontend\src\pages\Calculators\components\VolumeMixer\VolumeMixer.js
import { useState } from "react";
import { toast } from "react-toastify";
import { DEFAULT_VALUES, PART_LABELS } from "./constants";

const VolumeMixer = ({ onCalculate }) => {
  const [parts, setParts] = useState({
    part1: DEFAULT_VALUES.part1,
    part2: DEFAULT_VALUES.part2,
    part3: DEFAULT_VALUES.part3,
  });
  const [finalVolume, setFinalVolume] = useState(DEFAULT_VALUES.finalVolume);
  const [isCalculating, setIsCalculating] = useState(false);

  const handlePartChange = (partId, value) => {
    setParts((prev) => ({
      ...prev,
      [partId]: value,
    }));
  };

  const handleCalculate = () => {
    const p1 = parseFloat(parts.part1) || 0;
    const p2 = parseFloat(parts.part2) || 0;
    const p3 = parseFloat(parts.part3) || 0;
    const fVolume = parseFloat(finalVolume);

    if (isNaN(fVolume) || fVolume <= 0) {
      toast.error("Финальный объем должен быть положительным числом");
      return;
    }

    if (p1 === 0 && p2 === 0 && p3 === 0) {
      toast.error("Хотя бы одна часть должна быть больше нуля");
      return;
    }

    setIsCalculating(true);

    const calculate = () => {
      const totalParts = p1 + p2 + p3;
      
      // Сначала точные объёмы
      const exactVolumes = {
        part1: (p1 / totalParts) * fVolume,
        part2: (p2 / totalParts) * fVolume,
        part3: (p3 / totalParts) * fVolume,
      };
      
      const roundedVolumes = {
        part1: Math.round(exactVolumes.part1 * 10) / 10,
        part2: Math.round(exactVolumes.part2 * 10) / 10,
        part3: exactVolumes.part3, 
      };
      
      const sumFirstTwo = roundedVolumes.part1 + roundedVolumes.part2;
      let thirdVolume = fVolume - sumFirstTwo;
      
      if (p3 === 0) {
        if (Math.abs(thirdVolume) > 0.01) {
          const correction = thirdVolume / 2;
          roundedVolumes.part1 = Math.round((roundedVolumes.part1 + correction) * 10) / 10;
          roundedVolumes.part2 = Math.round((roundedVolumes.part2 + correction) * 10) / 10;
          thirdVolume = fVolume - (roundedVolumes.part1 + roundedVolumes.part2);
        }
      }
      
      roundedVolumes.part3 = Math.round(thirdVolume * 10) / 10;
      
      const finalSum = roundedVolumes.part1 + roundedVolumes.part2 + roundedVolumes.part3;
      if (Math.abs(finalSum - fVolume) > 0.1) {
        roundedVolumes.part3 = Math.round((roundedVolumes.part3 + fVolume - finalSum) * 10) / 10;
      }
      
      const proportionParts = [p1, p2, p3].filter(v => v > 0);
      const proportionString = proportionParts.join(':');
      
      const componentsToShow = PART_LABELS.filter(part => parts[part.id] > 0);
      
      onCalculate(
        <div className="text-center py-3">
          <h5 className="mb-3">Результат смешивания</h5>
          <div className="mb-3 fs-6">
            Для получения <strong>{fVolume} мл</strong> рабочего раствора в пропорции <strong>{proportionString}</strong> необходимо смешать компоненты в следующих объёмах:
          </div>
          <div className="d-flex flex-column gap-2 mb-3">
            {componentsToShow.map((part) => (
              <div key={part.id} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <span className="fw-medium">{part.label}:</span>
                <span className="fw-bold text-primary fs-5">
                  {roundedVolumes[part.id]} мл
                </span>
              </div>
            ))}
          </div>
          {Math.abs(finalSum - fVolume) > 0.01 && (
            <div className="text-muted small mt-2">
              * Сумма: {finalSum} мл (погрешность округления {Math.round(Math.abs(finalSum - fVolume) * 10) / 10} мл)
            </div>
          )}
        </div>
      );
      
      setIsCalculating(false);
    };
    
    setTimeout(calculate, 50);

  };

  return (
    <div className="container">
      <h2 className="mb-4">Калькулятор смешивания растворов</h2>

      <div className="alert alert-info">
        Введите пропорции компонентов и желаемый объём готового раствора.
        <br/><br/>
        <strong>Совет:</strong> Для точного определения объёмов используйте мерный цилиндр
      </div>

      <div className="row g-3 mb-4">
        {PART_LABELS.map((part) => (
          <div key={part.id} className="col-md-4">
            <label className="form-label">{part.label}</label>
            <input
              type="number"
              className="form-control"
              value={parts[part.id]}
              onChange={(e) => handlePartChange(part.id, e.target.value)}
              min="0"
              step="0.1"
              placeholder="0"
            />
          </div>
        ))}
      </div>

      <div className="mb-4">
        <label className="form-label">Объём готового раствора (мл):</label>
        <input
          type="number"
          className="form-control"
          value={finalVolume}
          onChange={(e) => setFinalVolume(e.target.value)}
          min="1"
          step="1"
        />
      </div>

      <button
        className="btn btn-primary w-100"
        onClick={handleCalculate}
        disabled={isCalculating}
      >
        {isCalculating ? "Вычисляем..." : "Рассчитать объемы"}
      </button>
    </div>
  );
};

export default VolumeMixer;